import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Appointment } from '@prody/shared';
import {
  Button, Code, EmptyState, IconPlus, ListPane, PageHeader, Stamp,
  Toolbar, Workbench,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useAppointments } from '~/platform/data/queries';
import './schedule.css';

/* ==================================================================
   Schedule.

   A week as seven ruled columns, not a month grid. This business
   plans in weeks — farm run days, delivery weekdays, tasting visits —
   and a month view is mostly empty cells in a trade where nothing is
   booked more than ten days out.
   ================================================================== */

const APPOINTMENT_TONE: Record<Appointment['kind'], 'accent' | 'info' | 'ok' | 'neutral'> = {
  'sales-visit': 'accent',
  tasting: 'accent',
  'farm-visit': 'ok',
  'delivery-slot': 'info',
  collection: 'info',
  internal: 'neutral',
  call: 'neutral',
};

export default function SchedulePage() {
  const { t } = useTranslation(['schedule', 'common']);
  const { business, format } = useBusiness();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(format.tradingDay()));

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const { data: appointments = [], isLoading } = useAppointments(
    business.id,
    weekStart,
    days[6] ?? weekStart,
  );

  const byDay = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const appointment of appointments) {
      const key = appointment.startsAt.slice(0, 10);
      map.set(key, [...(map.get(key) ?? []), appointment]);
    }
    for (const list of map.values()) list.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    return map;
  }, [appointments]);

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={`${format.date(weekStart)} – ${format.date(days[6] ?? weekStart)}`}
        actions={
          <Button variant="primary" icon={<IconPlus />}>
            {t('newAppointment')}
          </Button>
        }
      />

      <Toolbar>
        <Button variant="quiet" size="sm" onClick={() => setWeekStart(addDays(weekStart, -7))}>
          ←
        </Button>
        <Button variant="quiet" size="sm" onClick={() => setWeekStart(startOfWeek(format.tradingDay()))}>
          {t('common:labels.today')}
        </Button>
        <Button variant="quiet" size="sm" onClick={() => setWeekStart(addDays(weekStart, 7))}>
          →
        </Button>
      </Toolbar>

      <ListPane>
        {!isLoading && appointments.length === 0 ? (
          <EmptyState title={t('empty')} body={t('emptyBody')} />
        ) : (
          <div className="pd-week">
            {days.map((day) => {
              const items = byDay.get(day) ?? [];
              const isToday = day === format.tradingDay();
              return (
                <section key={day} className="pd-week__day" data-today={isToday || undefined}>
                  <header className="pd-week__day-head">
                    <span className="stencil">{format.date(day, 'weekday')}</span>
                    {items.length > 0 ? <span className="num faint">{items.length}</span> : null}
                  </header>

                  <ul className="pd-week__items">
                    {items.map((appointment) => (
                      <li key={appointment.id}>
                        <article className="pd-appt" data-tone={APPOINTMENT_TONE[appointment.kind]}>
                          <span className="pd-band" aria-hidden="true" style={{ background: 'var(--tone)' }} />
                          <div className="pd-appt__body">
                            <span className="num pd-appt__time">
                              {format.dateTime(appointment.startsAt, 'time')}
                            </span>
                            <h3 className="pd-appt__title">{appointment.title}</h3>
                            {appointment.partyName ? (
                              <p className="pd-appt__party truncate">{appointment.partyName}</p>
                            ) : null}
                            <div className="pd-appt__foot">
                              <Stamp tone={APPOINTMENT_TONE[appointment.kind]} size="sm">
                                {t(`kind.${appointment.kind}`)}
                              </Stamp>
                              {appointment.location ? <Code>{appointment.location}</Code> : null}
                            </div>
                          </div>
                        </article>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </ListPane>
    </Workbench>
  );
}

const addDays = (plain: string, days: number): string => {
  const at = new Date(`${plain}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
};

/** Monday-first, matching every European market week. */
const startOfWeek = (plain: string): string => {
  const at = new Date(`${plain}T12:00:00Z`);
  const shift = (at.getUTCDay() + 6) % 7;
  return addDays(plain, -shift);
};
